import { Link } from "react-router-dom";
import {
  AppWindow,
  ArrowRight,
  LifeBuoy,
  MessagesSquare,
  Palette,
  Search,
  Smartphone,
  Target,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { PortfolioGrid } from "@/components/PortfolioGrid";
import { QuoteForm } from "@/components/QuoteForm";
import { Reveal } from "@/components/Reveal";
import { FAQ_ITEMS, PROCESS_STEPS, SERVICES, TICKER_ITEMS, WHY_ITEMS } from "@/data/content";

const WHY_ICONS = { Target, Palette, Smartphone, AppWindow, Search, LifeBuoy, MessagesSquare };

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Koranex — Création de sites web, applications mobiles & SEO"
        description="Koranex conçoit des sites web professionnels, des applications mobiles et des stratégies SEO pour développer votre activité. Devis gratuit et sans engagement."
      />

      {/* HERO */}
      <header className="py-16 md:py-16 md:py-24 px-6 max-w-6xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0f172a] border border-slate-800 text-xs font-mono-x text-[#38bdf8] mb-6 kx-up">
            <span>INGÉNIERIE DIGITALE &amp; CROISSANCE</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl md:text-8xl text-white leading-none mb-6 kx-up delay-100">
            Votre présence numérique mérite mieux.
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10 kx-up delay-200">
            Koranex conçoit des sites web professionnels, des applications mobiles et des stratégies SEO pour aider
            votre entreprise à gagner en visibilité et à développer son activité.
          </p>
          <div className="flex flex-wrap justify-center gap-4 kx-up delay-300">
            <Link
              to="/contact"
              data-testid="hero-quote-button"
              className="px-8 py-4 rounded-xl bg-[#1d4ed8] hover:bg-blue-600 text-white font-mono-x text-xs uppercase tracking-wider font-bold transition-colors shadow-xl shadow-blue-950 kx-glow kx-shine"
            >
              Demander un devis gratuit
            </Link>
            <Link
              to="/realisations"
              data-testid="hero-portfolio-button"
              className="px-8 py-4 rounded-xl bg-[#0f172a] hover:bg-slate-800 border border-slate-800 text-slate-300 font-mono-x text-xs uppercase tracking-wider transition-colors"
            >
              Découvrir nos réalisations
            </Link>
          </div>
        </div>

        {/* Set-piece visuel : maquettes ordinateur + smartphone */}
        <div className="mt-16 rounded-2xl overflow-hidden border border-slate-800 bg-[#0f172a] relative shadow-2xl kx-up delay-300">
          <div
            className="absolute inset-0 kx-drift"
            style={{
              background:
                "radial-gradient(100% 80% at 50% 40%, rgba(29,78,216,0.25) 0%, transparent 70%), linear-gradient(135deg, #020617, #0f172a)",
            }}
          />
          <div className="relative p-6 md:p-12 md:pb-16">
            <img
              src="/mockups/hero-laptop.webp"
              alt="Maquette d'un site web professionnel conçu par Koranex, présentée sur un ordinateur portable"
              className="w-full md:w-3/4 rounded-xl border border-slate-800 shadow-2xl"
            />
            <img
              src="/mockups/hero-phone.webp"
              alt="Maquette d'application mobile moderne conçue par Koranex, présentée sur un smartphone"
              className="mt-4 md:mt-0 md:absolute md:bottom-6 md:right-8 w-full sm:w-2/3 md:w-[30%] rounded-xl border border-slate-800 shadow-2xl"
            />
          </div>
          <div className="relative border-t border-slate-800 px-6 py-3 flex items-center justify-between text-[10px] font-mono-x uppercase tracking-widest text-slate-500">
            <span>Sites web · Applications mobiles · SEO · Maintenance</span>
            <span className="text-[#38bdf8]">Devis gratuit</span>
          </div>
        </div>
      </header>

      {/* TICKER */}
      <div className="border-y border-slate-800 bg-[#050b18] py-4 overflow-hidden" aria-hidden="true">
        <div className="kx-marquee items-center gap-12 font-mono-x text-xs uppercase tracking-widest text-slate-400">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center gap-12">
              <span>{item}</span>
              <span className="text-[#1d4ed8]">/</span>
            </span>
          ))}
        </div>
      </div>

      {/* EXPERTISES */}
      <section className="py-16 md:py-24 px-6 max-w-6xl mx-auto" data-testid="services-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 md:mb-16 gap-4">
          <div>
            <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest">Solutions Techniques</span>
            <h2 className="font-display text-4xl sm:text-6xl text-white mt-1">Nos quatre expertises</h2>
          </div>
          <p className="text-xs font-mono-x text-slate-400 max-w-xs">
            Développement robuste et design sur mesure pour chaque étape de votre croissance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SERVICES.map((s, idx) => (
            <Reveal key={s.slug} delay={idx * 0.12}>
              <div
                data-testid={`service-card-${s.slug}`}
                className="group kx-card h-full p-8 rounded-2xl bg-[#0f172a] border border-slate-800 hover:border-[#38bdf8]/60 transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between"
              >
                <div className="relative">
                  <span className="text-xs font-mono-x text-[#38bdf8] inline-block transition-all duration-300 group-hover:tracking-[0.2em]">{s.kicker}</span>
                  <h3 className="text-xl font-bold text-white mt-3 mb-2 transition-colors duration-300 group-hover:text-[#38bdf8]">{s.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-6 transition-colors duration-300 group-hover:text-slate-300">{s.short}</p>
                </div>
                <Link
                  to={`/${s.slug}`}
                  data-testid={`service-cta-${s.slug}`}
                  className="relative inline-flex items-center gap-2 text-xs font-mono-x font-bold uppercase tracking-wider text-[#38bdf8] hover:text-white transition-colors"
                >
                  {s.cta} <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* RÉALISATIONS */}
      <section className="py-16 md:py-24 bg-[#050b18] border-t border-slate-800 px-6" data-testid="portfolio-section">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 md:mb-16 gap-4">
            <div>
              <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest">Démonstrations</span>
              <h2 className="font-display text-4xl sm:text-6xl text-white mt-1">Maquettes &amp; réalisations</h2>
            </div>
            <Link
              to="/realisations"
              data-testid="portfolio-see-all"
              className="inline-flex items-center gap-2 text-xs font-mono-x font-bold uppercase tracking-wider text-[#38bdf8] hover:text-white transition-colors"
            >
              Toute la galerie <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <PortfolioGrid limit={6} />
          <p className="text-[11px] font-mono-x text-slate-500 mt-8 text-center">
            Les projets présentés sont des concepts de démonstration illustrant le savoir-faire de Koranex.
          </p>
        </div>
      </section>

      {/* POURQUOI KORANEX */}
      <section className="py-16 md:py-24 px-6 max-w-6xl mx-auto" data-testid="why-section">
        <div className="mb-10 md:mb-16">
          <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest">Vos avantages</span>
          <h2 className="font-display text-4xl sm:text-6xl text-white mt-1">Pourquoi choisir Koranex ?</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {WHY_ITEMS.map((item) => {
            const Icon = WHY_ICONS[item.icon];
            return (
              <div
                key={item.title}
                data-testid={`why-item-${item.icon}`}
                className="p-6 rounded-2xl bg-[#0f172a] border border-slate-800 hover:border-[#1d4ed8] transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-[#1d4ed8]/20 border border-[#38bdf8]/30 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#38bdf8]" />
                </div>
                <h3 className="font-bold text-white text-base mb-1">{item.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{item.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* PROCESSUS */}
      <section className="py-16 md:py-24 bg-[#050b18] border-t border-slate-800 px-6" data-testid="process-section">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 md:mb-16 text-center">
            <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest">Méthodologie</span>
            <h2 className="font-display text-4xl sm:text-6xl text-white mt-1">Notre méthode de travail</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {PROCESS_STEPS.map((step, i) => (
              <div key={step.title} className="p-6 rounded-xl bg-[#0f172a] border border-slate-800 relative">
                <span className="text-3xl font-display text-[#1d4ed8]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-bold text-white text-base mt-2 mb-1">{step.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 px-6 max-w-6xl mx-auto" data-testid="cta-section">
        <div className="rounded-2xl border border-[#1d4ed8]/40 bg-gradient-to-br from-[#0f172a] to-[#020617] p-10 md:p-16 text-center relative overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(60% 80% at 50% 0%, rgba(56,189,248,0.12) 0%, transparent 70%)" }}
          />
          <h2 className="font-display text-3xl sm:text-5xl text-white mb-4 relative">
            Vous avez un projet numérique ? Parlons-en.
          </h2>
          <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto mb-8 relative">
            Que vous souhaitiez créer un site web, lancer une application mobile ou améliorer votre visibilité sur
            Google, Koranex vous accompagne de la conception à la mise en ligne.
          </p>
          <Link
            to="/contact"
            data-testid="cta-quote-button"
            className="relative inline-flex px-8 py-4 rounded-xl bg-[#1d4ed8] hover:bg-blue-600 text-white font-mono-x text-xs uppercase tracking-wider font-bold transition-colors shadow-xl shadow-blue-950 kx-glow kx-shine"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-[#050b18] border-t border-slate-800 px-6" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12 text-center">
            <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest">Questions fréquentes</span>
            <h2 className="font-display text-4xl sm:text-5xl text-white mt-1">Tout ce que vous devez savoir</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border border-slate-800 rounded-xl bg-[#0f172a] px-5 data-[state=open]:border-[#1d4ed8]"
              >
                <AccordionTrigger
                  data-testid={`faq-question-${i}`}
                  className="text-sm font-bold text-white hover:no-underline hover:text-[#38bdf8] text-left"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent data-testid={`faq-answer-${i}`} className="text-sm text-slate-400 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONTACT */}
      <section className="py-16 md:py-24 px-6" data-testid="contact-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-mono-x uppercase text-[#38bdf8] tracking-widest">Devis gratuit</span>
            <h2 className="font-display text-3xl sm:text-5xl text-white mt-1 mb-3">Parlez-nous de votre projet</h2>
            <p className="text-xs font-mono-x text-slate-400">
              Recevez votre devis gratuit et personnalisé, sans engagement.
            </p>
          </div>
          <QuoteForm formId="home-quote" />
        </div>
      </section>
    </div>
  );
}
